import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import qs from "qs";
import { toastError, toastSuccess } from "../../shared/toastHelper";

const PER_PAGE = 25;

export const ClassesIndexTableContext = createContext(null);

export const useClassesIndexTableContext = () => useContext(ClassesIndexTableContext);

export const archivedStatusQuery = (status) => {
  if (!Array.isArray(status) || status.length === 0) return { active: true };
  if (status.length > 1) return {};
  return status[0] === "archived" ? { archived_at_not_null: true } : { active: true };
};

const hasFilterValues = (filters) => Object.values(filters).some((value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") return Object.values(value).some((part) => part !== "" && part != null);
  return Boolean(value);
});

const readStorage = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    return null;
  }
};

const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1);

const withNewColumns = (columns, visible, hidden) => {
  const known = new Set([...visible, ...hidden].map((c) => c.id));
  return [...hidden, ...columns.filter((c) => !known.has(c.id) && !c.alwaysVisible)];
};

export default function useClassesIndexTable({
  indexPath, csrfToken, teamId, storageKey, dataKey, noun, kind, columns, sortOptions, filterGroups, searchKey = "name_cont", buildQuery, buildParams,
}) {
  const defaultVisible = () => columns.filter((c) => c.defaultVisible);
  const defaultHidden = () => columns.filter((c) => !c.defaultVisible && !c.alwaysVisible);
  const columnsKey = `${storageKey}Columns_${teamId}`;
  const viewsKey = `${storageKey}Views_${teamId}`;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState({});
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(defaultVisible);
  const [hiddenColumns, setHiddenColumns] = useState(defaultHidden);
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const [sortBy, setSortBy] = useState("name asc");
  const [savedViews, setSavedViews] = useState([]);
  const [currentView, setCurrentView] = useState(null);
  const [hasViewChanges, setHasViewChanges] = useState(false);
  const [pendingArchive, setPendingArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const baselineViewRef = useRef(null);
  const loadIdRef = useRef(0);
  const cancelRef = useRef(null);
  const requestRef = useRef({ buildQuery, buildParams });
  useLayoutEffect(() => {
    requestRef.current = { buildQuery, buildParams };
  });

  useEffect(() => {
    const t = setTimeout(() => setQuery(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [query, activeFilters, sortBy]);

  const load = useCallback(async () => {
    cancelRef.current?.("superseded by a newer load");
    const source = axios.CancelToken.source();
    cancelRef.current = source.cancel;
    const loadId = (loadIdRef.current += 1);
    const isStale = () => loadIdRef.current !== loadId;
    const { buildQuery: toQuery, buildParams: toParams } = requestRef.current;
    const q = { ...toQuery(activeFilters), ...(query ? { [searchKey]: query } : {}), ...(sortBy ? { s: sortBy } : {}) };
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(indexPath, {
        params: { format: "json", q, ...toParams?.(activeFilters), per_page: PER_PAGE, page },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "brackets" }),
        cancelToken: source.token,
      });
      if (isStale()) return;
      setRows(resp.data[dataKey]);
      setPagination(resp.data.pagination);
      setLoaded(true);
    } catch (e) {
      if (axios.isCancel?.(e) || e.name === "CanceledError" || e.code === "ERR_CANCELED") return;
      if (isStale()) return;
      setError(`Could not load ${noun}.`);
      toastError(`Could not load ${noun}.`);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [indexPath, query, activeFilters, sortBy, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!teamId) return;
    const saved = readStorage(columnsKey);
    if (saved?.visible && saved?.hidden) restoreColumns(saved.visible.map((c) => c.id), saved.hidden.map((c) => c.id));
    setSavedViews(readStorage(viewsKey) || []);
  }, [teamId]);

  useEffect(() => {
    const baseline = baselineViewRef.current;
    setHasViewChanges(!!baseline && (
      visibleColumns.map((c) => c.id).join(",") !== baseline.visibleColumns.join(",")
      || JSON.stringify(activeFilters) !== JSON.stringify(baseline.activeFilters)
      || sortBy !== baseline.sortBy
    ));
  }, [visibleColumns, activeFilters, sortBy]);

  function restoreColumns(visibleIds = [], hiddenIds = []) {
    const columnsById = new Map(columns.map((column) => [column.id, column]));
    const inSavedOrder = (ids) => ids.map((id) => columnsById.get(id)).filter(Boolean);
    const visible = inSavedOrder(visibleIds);
    setVisibleColumns(visible);
    setHiddenColumns(withNewColumns(columns, visible, inSavedOrder(hiddenIds)));
    return visible.map((c) => c.id);
  }

  const changeColumns = (visible, hidden) => {
    setVisibleColumns(visible);
    setHiddenColumns(hidden);
    if (teamId) localStorage.setItem(columnsKey, JSON.stringify({ visible, hidden }));
  };

  const storeViews = (views) => {
    setSavedViews(views);
    if (teamId) localStorage.setItem(viewsKey, JSON.stringify(views));
  };

  const applyView = (view) => {
    setCurrentView(view?.name ?? null);
    baselineViewRef.current = view && { visibleColumns: view.visibleFields, activeFilters: view.activeFilters || {}, sortBy: view.sortBy };
    setHasViewChanges(false);
  };

  const saveView = (name) => {
    const view = {
      name,
      visibleFields: visibleColumns.map((c) => c.id),
      hiddenFields: hiddenColumns.map((c) => c.id),
      activeFilters: { ...activeFilters },
      sortBy,
    };
    storeViews([...savedViews.filter((v) => v.name !== name), view]);
    applyView(view);
  };

  const loadView = (view) => {
    const restored = { ...view, visibleFields: restoreColumns(view.visibleFields, view.hiddenFields), sortBy: view.sortBy || sortBy };
    setActiveFilters(restored.activeFilters || {});
    setSortBy(restored.sortBy);
    applyView(restored);
  };

  const deleteView = (view) => {
    storeViews(savedViews.filter((v) => v.name !== view.name));
    if (currentView === view.name) applyView(null);
  };

  const removeFilter = (filterId) => {
    setActiveFilters((current) => {
      const next = { ...current };
      delete next[filterId];
      return next;
    });
  };

  const archive = useCallback(async (record) => {
    const done = record.archived ? "unarchived" : "archived";
    try {
      await axios.post(`${indexPath}/${record.id}/${record.archived ? "unarchive" : "archive"}`, {}, { headers: { "X-CSRF-Token": csrfToken } });
      toastSuccess(`${capitalize(kind)} ${done}`);
      load();
    } catch (e) {
      toastError(`Could not update ${kind}.`);
    }
  }, [indexPath, csrfToken, kind, load]);

  const requestArchive = useCallback((record) => (record.archived ? archive(record) : setPendingArchive(record)), [archive]);

  const confirmArchive = async () => {
    setArchiving(true);
    await archive(pendingArchive);
    setArchiving(false);
    setPendingArchive(null);
  };

  const totalEntries = pagination?.total_entries ?? rows.length;

  return {
    indexPath,
    noun,
    nounTitle: capitalize(noun),
    kind,
    sortOptions,
    filterGroups,
    rows,
    loading,
    loaded,
    error,
    load,
    search,
    setSearch,
    activeFilters,
    setActiveFilters,
    removeFilter,
    filtering: Boolean(query) || hasFilterValues(activeFilters),
    filterSidebarOpen,
    setFilterSidebarOpen,
    page,
    setPage,
    totalEntries,
    totalPages: pagination?.total_pages || 1,
    rangeStart: totalEntries === 0 ? 0 : (page - 1) * PER_PAGE + 1,
    rangeEnd: Math.min(page * PER_PAGE, totalEntries),
    visibleColumns,
    hiddenColumns,
    changeColumns,
    tableSettingsOpen,
    setTableSettingsOpen,
    sortBy,
    setSortBy,
    savedViews,
    currentView,
    hasViewChanges,
    saveView,
    loadView,
    deleteView,
    requestArchive,
    pendingArchive,
    setPendingArchive,
    archiving,
    confirmArchive,
  };
}
